import { useCallback, useMemo, type MouseEvent, type ReactElement } from 'react';
import DownloadIcon from '@mui/icons-material/GetApp';
import {
    fetchRelatedRecords,
    useDataProvider,
    useNotify,
    useListContext,
    useResourceContext,
    Button,
} from 'react-admin';
import type { SortPayload, Exporter, FilterPayload, ButtonProps } from 'react-admin';

/** Large page size for CSV export so filtered exports are not capped at the RA default (1000). */
export const LIST_EXPORT_MAX_RESULTS = 100000;

const defaultIcon = <DownloadIcon />;

const sanitizeRestProps = ({
    filterValues,
    resource,
    maxResults,
    exporter,
    ...rest
}: Omit<ListExportButtonProps, 'sort' | 'label' | 'icon'>) => rest;

export const ListExportButton = (props: ListExportButtonProps) => {
    const {
        maxResults = LIST_EXPORT_MAX_RESULTS,
        onClick,
        label = 'ra.action.export',
        icon = defaultIcon,
        exporter: customExporter,
        ...rest
    } = props;
    const { filter, filterValues, sort, exporter: exporterFromContext, total } = useListContext(props);
    const resource = useResourceContext(props);
    const exporter =
        customExporter ??
        (exporterFromContext && typeof exporterFromContext === 'function' ? exporterFromContext : undefined);
    const dataProvider = useDataProvider();
    const notify = useNotify();

    const mergedFilter: FilterPayload = useMemo(() => {
        if (filter && typeof filter === 'object' && !Array.isArray(filter)) {
            return { ...(filterValues ?? {}), ...filter };
        }
        return filterValues ?? {};
    }, [filter, filterValues]);

    const handleClick = useCallback(
        (event: MouseEvent) => {
            dataProvider
                .getList(resource, {
                    sort,
                    filter: mergedFilter,
                    pagination: { page: 1, perPage: maxResults },
                })
                .then(({ data }) =>
                    exporter?.(data, fetchRelatedRecords(dataProvider), dataProvider, resource)
                )
                .catch((error: unknown) => {
                    console.error(error);
                    notify('ra.notification.http_error', { type: 'warning' });
                });
            if (typeof onClick === 'function') {
                onClick(event as unknown as Event);
            }
        },
        [dataProvider, exporter, mergedFilter, maxResults, notify, onClick, resource, sort]
    );

    return (
        <Button onClick={handleClick} label={label} disabled={total === 0} {...sanitizeRestProps(rest)}>
            {icon}
        </Button>
    );
};

interface Props {
    exporter?: Exporter;
    filterValues?: FilterPayload;
    icon?: ReactElement;
    label?: string;
    maxResults?: number;
    onClick?: (e: Event) => void;
    resource?: string;
    sort?: SortPayload;
}

export type ListExportButtonProps = Props & ButtonProps;
